import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Play, Plus, X } from "lucide-react";
import { z } from "zod";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { TimeInput } from "@/components/ui/time-input";
import { toast } from "@/components/ui/use-toast";
import { buildSlicesIntradayAlgoBuildSlicesPost as buildSlices } from "@/client-admin/services.gen";

const downtimeFormSchema = z.object({
  blocked_windows: z
    .array(z.object({ start: z.string(), end: z.string() }))
    .max(10, "At most 10 downtime windows")
    .superRefine((arr, ctx) => {
      arr.forEach((w, i) => {
        const startSet = w.start !== "";
        const endSet = w.end !== "";
        // Fully empty rows are dropped at submit time, not flagged.
        if (!startSet && !endSet) return;
        if (startSet !== endSet) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Both start and end are required",
            path: [i, startSet ? "end" : "start"],
          });
          return;
        }
        if (w.end <= w.start) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "End must be after start",
            path: [i, "end"],
          });
        }
      });
    }),
});

type DowntimeFormValues = z.infer<typeof downtimeFormSchema>;

interface BuildSlicesDialogProps {
  deliveryDate: string;
  algoCount: number;
  canBuild: boolean;
}

export function BuildSlicesDialog({ deliveryDate, algoCount, canBuild }: BuildSlicesDialogProps) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<DowntimeFormValues>({
    resolver: zodResolver(downtimeFormSchema),
    defaultValues: { blocked_windows: [] },
  });
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "blocked_windows",
  });

  const { mutateAsync: buildSlicesMutation, isPending } = useMutation({
    mutationFn: buildSlices,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ predicate: (q) => (q.queryKey[0] as string)?.startsWith("intraday-algo") });
      toast({
        title: "Success",
        description: `Built ${data.total_inserts} slice order(s) for ${deliveryDate}.`,
      });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.body?.detail ?? "Failed to build slices. Please try again later.",
        variant: "destructive",
      });
    },
  });

  const handleOpenChange = (v: boolean) => {
    if (isPending) return;
    if (!v) form.reset({ blocked_windows: [] });
    setOpen(v);
  };

  const handleSubmit = async (values: DowntimeFormValues) => {
    const filledWindows = values.blocked_windows.filter((w) => w.start !== "" && w.end !== "");
    await buildSlicesMutation({
      requestBody: {
        delivery_date: deliveryDate,
        ...(filledWindows.length > 0 ? { blocked_windows: filledWindows } : {}),
      },
    });
    setOpen(false);
    form.reset({ blocked_windows: [] });
  };

  const disabled = algoCount === 0 || !canBuild;
  const triggerTitle = !canBuild
    ? "Pause the worker before building slices"
    : algoCount === 0
      ? "Set at least one signal to Algo 1 first"
      : undefined;

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>
        <Button size="sm" disabled={disabled || isPending} title={triggerTitle}>
          <Play className="mr-1 h-4 w-4" />
          Build Slices
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <AlertDialogHeader>
              <AlertDialogTitle>Build Slices</AlertDialogTitle>
              <AlertDialogDescription>
                Create algo trade slices for delivery date <strong>{deliveryDate}</strong> based on {algoCount}{" "}
                signal(s) with Algo=1. If slices already exist for any PTU, active orders will be cancelled and slices
                recalculated with the remaining volume.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <div className="space-y-2">
              <FormLabel>Downtime windows (optional)</FormLabel>
              <FormDescription>
                Exclude time periods (e.g. broker outage). Slices will be redistributed across the remaining time.
              </FormDescription>

              {fields.map((field, index) => (
                <div key={field.id} className="flex items-start gap-2">
                  <FormField
                    control={form.control}
                    name={`blocked_windows.${index}.start`}
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <TimeInput aria-label={`Window ${index + 1} start`} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <span className="pt-2">—</span>
                  <FormField
                    control={form.control}
                    name={`blocked_windows.${index}.end`}
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <TimeInput aria-label={`Window ${index + 1} end`} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => remove(index)}
                    aria-label={`Remove window ${index + 1}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}

              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => append({ start: "", end: "" })}
                disabled={fields.length >= 10}
              >
                <Plus className="mr-1 h-4 w-4" />
                Add downtime window
              </Button>
            </div>

            <AlertDialogFooter>
              <AlertDialogCancel type="button">Cancel</AlertDialogCancel>
              <Button type="submit" disabled={isPending}>
                {isPending ? (
                  <>
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    Building...
                  </>
                ) : (
                  "Confirm"
                )}
              </Button>
            </AlertDialogFooter>
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
